import { useState } from 'react';
import type { Media } from '../../types';
import { MediaLightbox } from './MediaLightbox';
import { getVideoThumbnail, isEmbeddableVideo } from '../../lib/media';

interface MediaGalleryProps {
  media: Media[];
}

export function MediaGallery({ media }: MediaGalleryProps) {
  const [active, setActive] = useState<Media | null>(null);

  if (!media || media.length === 0) return null;

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {media.map((m) => {
          const url = m.downloadUrl || m.externalUrl;
          const thumbnail = getVideoThumbnail(url);
          const isVideo = m.type === 'video';
          const isEmbedVideo = isVideo && isEmbeddableVideo(url);

          return (
            <button
              key={m.mediaId}
              onClick={() => setActive(m)}
              className="rounded-lg border border-white/10 bg-white/5 p-2 text-left hover:bg-white/10 transition-colors"
            >
              <div className="relative aspect-video rounded-md border border-white/10 overflow-hidden">
                {isVideo ? (
                  // Video thumbnail with play overlay - clicking opens lightbox
                  <>
                    {thumbnail ? (
                      <img
                        className="absolute inset-0 h-full w-full object-cover"
                        src={thumbnail}
                        alt={m.caption || 'Video thumbnail'}
                        referrerPolicy="no-referrer"
                      />
                    ) : isEmbedVideo ? (
                      // Embed video without thumbnail (e.g., Vimeo) - show placeholder
                      <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
                        <span className="text-xs text-slate-400">Video</span>
                      </div>
                    ) : (
                      // Direct video file - use video element as poster
                      <video
                        className="absolute inset-0 h-full w-full object-cover"
                        src={url}
                        muted
                        playsInline
                        controls={false}
                        preload="metadata"
                      />
                    )}
                    {/* Play button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                        <svg className="w-5 h-5 text-slate-800 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </>
                ) : m.type === 'document' ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/5 text-xs text-slate-300">
                    <span className="px-3 py-1 rounded bg-primary-500/20 text-primary-100">
                      PDF / doc
                    </span>
                  </div>
                ) : (
                  <img
                    className="absolute inset-0 h-full w-full object-cover"
                    src={url}
                    alt={m.caption || ''}
                    referrerPolicy="no-referrer"
                  />
                )}
              </div>
              <p className="text-[11px] text-slate-300 mt-1 truncate">
                {m.caption || m.storagePath || m.externalUrl}
              </p>
            </button>
          );
        })}
      </div>

      <MediaLightbox media={active} onClose={() => setActive(null)} />
    </div>
  );
}
